import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import PageHero from "@/components/sections/PageHero";
import ContentSection from "@/components/sections/ContentSection";
import SectionHeader from "@/components/sections/SectionHeader";
import CTABlock from "@/components/sections/CTABlock";
import blogHero from "@/assets/blog-hero.jpg";

const categories = [
  "All",
  "Buying a Home",
  "Selling Your Home",
  "Real Estate Investing",
  "Dominican Republic Investing",
  "Property Preparation",
] as const;

type Category = (typeof categories)[number];

interface Article {
  title: string;
  excerpt: string;
  category: Category;
}

const articles: Article[] = [
  {
    title: "5 Things Every First-Time Buyer Should Know Before Starting Their Search",
    excerpt:
      "Buying your first home is exciting, but preparation is everything. From understanding pre-approval to knowing what to prioritize during showings, these five essentials will help you avoid costly mistakes and feel confident from day one.",
    category: "Buying a Home",
  },
  {
    title: "How to Price Your Home to Sell—Without Leaving Money on the Table",
    excerpt:
      "Pricing your home correctly is the most important decision you'll make when selling. Learn how strategic pricing attracts serious buyers, creates competition, and ultimately puts more money in your pocket.",
    category: "Selling Your Home",
  },
  {
    title: "Why the Dominican Republic Is One of the Best-Kept Secrets in Real Estate Investing",
    excerpt:
      "With a growing tourism market, favorable tax incentives, and stunning beachfront properties, the Dominican Republic offers exceptional opportunities for investors looking beyond the U.S. market.",
    category: "Dominican Republic Investing",
  },
  {
    title: "The Hidden ROI of Professional Property Preparation",
    excerpt:
      "Most sellers underestimate the impact of professional cleaning, painting, and minor repairs. Discover how investing in property preparation consistently delivers a measurable return at closing.",
    category: "Property Preparation",
  },
  {
    title: "Understanding the Massachusetts Real Estate Market in 2025",
    excerpt:
      "Inventory levels, interest rates, and buyer demand are shifting. Here's what you need to know about the current Massachusetts market and how to position yourself for success whether you're buying or selling.",
    category: "Real Estate Investing",
  },
  {
    title: "What to Expect During the Closing Process: A Step-by-Step Guide",
    excerpt:
      "The closing process can feel overwhelming, especially for first-time buyers and sellers. This step-by-step guide breaks down every stage so you know exactly what's coming and how to prepare.",
    category: "Buying a Home",
  },
];

const Blog = () => {
  const [activeCategory, setActiveCategory] = useState<Category>("All");

  useEffect(() => {
    document.title = "Blog | Real Estate Insights & Guidance | Hadassah Homes";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", "Expert real estate advice for buyers, sellers, and investors. Stay informed with the latest insights and market updates from Hadassah Homes.");
    }
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = "https://hadassahhomesinc.com/blog";
  }, []);

  const filtered = activeCategory === "All" ? articles : articles.filter((a) => a.category === activeCategory);
  const featured = articles.slice(0, 3);

  return (
    <SiteLayout>
      <PageHero eyebrow="Blog" title="Insights & Guidance" subtitle="Expert Real Estate Advice for Buyers, Sellers & Investors" backgroundImage={blogHero} />

      {/* SECTION 2 — INTRO */}
      <ContentSection>
        <div className="mx-auto max-w-prose space-y-6 text-lg md:text-xl leading-relaxed text-muted-foreground">
          <p>Stay informed with the latest insights, market updates, and expert guidance from Hadassah Homes.</p>
          <p>Whether you're buying your first home, preparing to sell, or exploring investment opportunities—our blog is designed to help you make smarter decisions with confidence.</p>
        </div>
      </ContentSection>

      {/* SECTION 3 — BROWSE BY CATEGORY */}
      <ContentSection variant="dark">
        <SectionHeader title="Browse by Category" />
        <div className="flex flex-wrap justify-center gap-3 md:gap-4">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`font-body text-sm uppercase tracking-widest px-6 py-3 border transition-colors ${
                activeCategory === cat
                  ? "bg-accent text-accent-foreground border-accent"
                  : "border-foreground/20 text-foreground hover:border-accent hover:text-accent"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </ContentSection>

      {/* SECTION 4 — BLOG ARTICLES GRID */}
      <ContentSection>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {filtered.map((article) => (
            <article key={article.title} className="bg-card border border-border flex flex-col">
              <div className="aspect-[16/10] bg-muted" />
              <div className="p-7 md:p-9 flex flex-col flex-1">
                <span className="font-body text-[10px] uppercase tracking-[0.3em] text-accent mb-3">{article.category}</span>
                <h3 className="font-headline text-xl md:text-2xl text-foreground mb-3 leading-tight">{article.title}</h3>
                <p className="text-muted-foreground text-base leading-relaxed mb-6 flex-1">{article.excerpt}</p>
                <span className="font-body text-sm uppercase tracking-widest text-accent hover:text-accent/80 transition-colors cursor-pointer">Read More →</span>
              </div>
            </article>
          ))}
        </div>
      </ContentSection>

      {/* SECTION 5 — FEATURED ARTICLES */}
      <ContentSection variant="dark">
        <SectionHeader title="Featured Articles" />
        <div className="space-y-10">
          {featured.map((article) => (
            <article key={article.title} className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 bg-card border border-border">
              <div className="aspect-[16/10] md:aspect-auto bg-muted" />
              <div className="p-7 md:p-9 flex flex-col justify-center">
                <span className="font-body text-[10px] uppercase tracking-[0.3em] text-accent mb-3">{article.category}</span>
                <h3 className="font-headline text-2xl md:text-3xl text-foreground mb-4 leading-tight">{article.title}</h3>
                <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-6">{article.excerpt}</p>
                <span className="font-body text-sm uppercase tracking-widest text-accent hover:text-accent/80 transition-colors cursor-pointer">Read More →</span>
              </div>
            </article>
          ))}
        </div>
      </ContentSection>

      {/* SECTION 6 — NOT SURE WHERE TO START */}
      <ContentSection>
        <SectionHeader title="Not Sure Where to Start?" />
        <div className="mx-auto max-w-prose text-center space-y-6">
          <p className="text-muted-foreground text-lg md:text-xl leading-relaxed">
            If you're new to real estate or just beginning to explore your options, start with our most popular guides:
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 md:gap-6">
            <Link to="/buy-a-home" className="font-body text-sm uppercase tracking-widest text-accent hover:text-accent/80 transition-colors">
              Buying a Home →
            </Link>
            <Link to="/sell-your-home" className="font-body text-sm uppercase tracking-widest text-accent hover:text-accent/80 transition-colors">
              Selling Your Home →
            </Link>
            <Link to="/invest" className="font-body text-sm uppercase tracking-widest text-accent hover:text-accent/80 transition-colors">
              Explore Investment Opportunities →
            </Link>
          </div>
        </div>
      </ContentSection>

      {/* SECTION 7 — FINAL CTA */}
      <CTABlock
        variant="dark"
        title="Have a Question We Haven't Covered?"
        description="We're always adding new content based on what our clients need. If there's a topic you'd like us to cover, let us know. Or book a consultation and get personalized guidance."
        primaryCta={{ text: "Book a Consultation →", href: "/consultation" }}
      />
    </SiteLayout>
  );
};

export default Blog;
